<template>
  <tailwind-layout title="Properties">
    <template #header>

      <div class="flex justify-between">
        <h2>Bank Accounts</h2>
        <div class="flex items-center space-x-4">
          <button v-if="!is_kyc" @click="createLinkToken('identity_verification')"
                  class="mt-1 sm:ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-gray-900 bg-gold-500 hover:bg-gold-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
              Verify Your Identity
          </button>
          <button v-if="is_kyc" @click="createLinkToken('auth')"
                  class="mt-1 sm:ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-gray-900 bg-gold-500 hover:bg-gold-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            Add New Bank Account
          </button>
        </div>
      </div>

    </template>
    <div class="bg-rent-risk-blue-100 overflow-hidden shadow-xl sm:rounded-lg p-6">
      <div class="rounded-md shadow overflow-x-auto">
        <div class="flex flex-col">
          <div class="overflow-x-auto">
            <div class="align-middle inline-block min-w-full">
              <div class="shadow overflow-hidden">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-rent-risk-blue-400 text-white">
                  <tr>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
                      Account Last 4 Digits
                    </th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
                      Bank Name
                    </th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
                      Linked Property
                    </th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
                      Missing Information
                    </th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
                    </th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="account in accounts" :key="account.id" class="bg-gray-50 border-b hover:bg-yellow-50">
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ account?.external_accounts?.data[0]?.last4 ?? 'N/A' }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {{ account?.external_accounts?.data[0]?.bank_name }}
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <InertiaLink
                          v-if="account?.property_id"
                          class="text-blue-600"
                          :href="route('rented-properties.edit', account?.property_id)"
                      > {{ account?.property_address }}
                      </InertiaLink>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <ul>
                        <li v-for="requirement in missing_information(account)" v-html="requirement">
                        </li>
                      </ul>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span
                          v-if="!account.requirements.eventually_due.length && !account.requirements.currently_due.length"
                          class="text-green-700">Completed</span>
                      <span v-else class="text-red-700">Incomplete</span>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <inertia-link
                          v-if="account.requirements.eventually_due.length || account.requirements.currently_due.length"
                          :title="account.requirements.eventually_due + 'are required'"
                          :href="route('connected-accounts.setup', account.id)" class="text-blue-600">
                        Resume Setup
                      </inertia-link>
                    </td>
                  </tr>
                  <tr v-show="!accounts.length">
                    <td colspan="5"
                        class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                      No bank accounts were found.
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <full-page-loader v-if="isLoader"></full-page-loader>
  </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import Button from "@/Jetstream/Button.vue";
import axios from "axios";
// import {Inertia} from "@inertiajs/inertia";
import FullPageLoader from "@/Shared/FullPageLoader.vue";

export default {
  components: {
    FullPageLoader,
    Button,
    Icon,
    Pagination,
    TailwindLayout
  },
  props: {
    accounts: Object,
    is_kyc: Boolean,
  },
  data() {
    return {
      isLoader: false,
    }

  },
  methods: {
    missing_information(account) {
      const combinedArray = [...account.requirements.currently_due, ...account.requirements.eventually_due];
      // Use Set to remove duplicates
      const uniqueArray = [...new Set(combinedArray)];

      // Loop through the array and modify values if they contain 'dob' or 'day'
      // Flag variables to check if day, month, or year is present
      let hasDob = false;

      const modifiedArray = uniqueArray.map(item => {
        // Return "Date of Birth" based on the presence of day, month, or year
        if (item.includes('dob') && !hasDob) {
          hasDob = true;
          return 'Date of Birth <small>(Resume Setup > Personal Information > Edit)</small>';
        } else if (item.includes('ssn_last_4')) {
          return 'SSN Last 4 <small>(Resume Setup > Personal Information > Edit)</small>';
        } else if (item.includes('verification') && item.includes('document')) {
          return 'Verification Document <small>(Resume Setup > Personal Information > Edit)</small>';
        } else {
          if (!item.includes('dob')) {
            const value = item.replace(/[_\.]/g, ' ')
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');

            return `${value} <small>(Resume Setup)`;
          }

        }
      });

      return modifiedArray;
    },

    async createLinkToken(product) {
      this.isLoader = true;
      try {
        const response = await axios.post(route('plaid.create-link-token'), {
          product: product,
        });
        if (response.data.status) {
          this.initializePlaidLink(response.data.public_token, product);
        }

        // Handle the successful response here
      } catch (error) {
        // Handle any errors here
        console.error('Error:', error.response ? error.response.data : error.message);
      }
    },

    async initializePlaidLink(public_token, product) {
      this.isLoader = false;
      var handler = Plaid.create({
        token: public_token,  // Use the provided public token directly
        onLoad: function () {
          // Optional, called when Link loads
        },
        onSuccess: (public_token, metadata) => {
          this.isLoader = true;
          if (product === 'auth') {
            this.finishAuth(public_token, metadata);
          } else if (product === 'identity_verification') {
            this.finishIdv(public_token, metadata);
          }
        },
        onExit: function (err, metadata) {
          // The user exited the Link flow.
          if (err != null) {
            // The user encountered a Plaid API error prior to exiting.
          }
          // metadata contains information about the institution
          // that the user selected and the most recent API request IDs.
          // Storing this information can be helpful for support.
        },
        onEvent: function (eventName, metadata) {
          // Optionally capture Link flow events, streamed through
          // this callback as your users connect an Item to Plaid.
          // For example:
          // eventName = "TRANSITION_VIEW"
          // metadata  = {
          //   link_session_id: "123-abc",
          //   mfa_type:        "questions",
          //   timestamp:       "2017-09-14T14:42:19.350Z",
          //   view_name:       "MFA",
          // }
        }
      });

      handler.open();
    },

    async finishAuth(public_token, metadata) {
      const response = await axios.post(route('plaid.exchange_public_token'), {
        public_token: public_token,
        metadata: metadata,
      });
      this.isLoader = false;
      Inertia.visit(response.data.redirect_link)
    },
    async finishIdv(public_token, metadata) {
      const response = await axios.post(route('plaid.idv.submit'), {
        public_token: public_token,
        metadata: metadata,
      });
      this.isLoader = false;

      // Handle the successful response here
      Inertia.reload();
    }
  },
}
</script>
